/**
 * Class to handle scroll spy functionality.
 */
export default class ScrollSpy {
    private lastKnowElement: string | null;
    private handlerRef: ((event: Event) => void) | null;

    constructor() {
        this.lastKnowElement = null;
        // Needed as variable to remove the event listeners
        this.handlerRef = null;
    }

    /**
     * Subscribes to relevant events for scroll spying.
     * @param dotnetReference - The .NET reference to invoke methods on.
     * @param containerSelector - The CSS selector of the container element.
     * @param sectionClassSelector - The CSS class selector of the section elements.
     */
    spying(dotnetReference: any, containerSelector: string, sectionClassSelector: string): void {
        this.lastKnowElement = null;

        this.handlerRef = this.handleScroll.bind(this, dotnetReference, containerSelector, sectionClassSelector);

        // Add the event for scroll. In case of zooming this event is also fired
        document.addEventListener('scroll', this.handlerRef, true);

        // A window resize could change the size of the relevant viewport
        window.addEventListener('resize', this.handlerRef, true);
    }

    /**
     * Handles the document scroll event and if needed, fires the .NET event.
     * @param dotnetReference - The .NET reference to invoke methods on.
     * @param containerSelector - The CSS selector of the container element.
     * @param sectionClassSelector - The CSS class selector of the section elements.
     * @param event - The scroll event.
     */
    handleScroll(dotnetReference: any, containerSelector: string, sectionClassSelector: string, event: Event): void {
        const container: Element | null = document.querySelector(containerSelector);
        if (container === null) {
            return;
        }

        const elements: HTMLCollectionOf<Element> = document.getElementsByClassName(sectionClassSelector);
        if (elements.length === 0) {
            return;
        }

        const containerTop: number = container.tagName === 'HTML' ? 0 : container.getBoundingClientRect().top;
        const containerHeight: number = container.clientHeight;
        const center: number = containerTop + containerHeight / 2.0;

        let minDifference: number = Number.MAX_SAFE_INTEGER;
        let foundAbove: boolean = false;
        let elementId: string = '';
        for (let i = 0; i < elements.length; i++) {
            const element: Element = elements[i];

            const rect: DOMRect = element.getBoundingClientRect();
            const diff: number = Math.abs(rect.top - center);

            if (!foundAbove && rect.top < center) {
                foundAbove = true;
                minDifference = diff;
                elementId = element.id;
                continue;
            }

            if (foundAbove && rect.top >= center) {
                continue;
            }

            if (diff < minDifference) {
                minDifference = diff;
                elementId = element.id;
            }
        }

        if (elementId !== this.lastKnowElement) {
            this.lastKnowElement = elementId;
            history.replaceState(null, '', window.location.pathname + "#" + elementId);
            dotnetReference.invokeMethodAsync('SectionChangeOccured', elementId);
        }
    }

    /**
     * Activates a section by updating the URL hash.
     * @param sectionId - The ID of the section to activate.
     */
    activateSection(sectionId: string): void {
        const element: HTMLElement | null = document.getElementById(sectionId);
        if (element) {
            this.lastKnowElement = sectionId;
            history.replaceState(null, '', window.location.pathname + "#" + sectionId);
        }
    }

    /**
     * Scrolls to a specific section smoothly.
     * @param sectionId - The ID of the section to scroll to.
     */
    scrollToSection(sectionId: string): void {
        if (sectionId) {
            const element: HTMLElement | null = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'start' });
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    /**
     * Removes event listeners for scroll spying.
     */
    unspy(): void {
        document.removeEventListener('scroll', this.handlerRef!, true);
        window.removeEventListener('resize', this.handlerRef!, true);
    }
}

